import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import AppHeader from "@/components/AppHeader";

const defaultCriteria = [
  { id: "clarity", name: "Clarity", weight: 25 },
  { id: "impact", name: "Impact", weight: 35 },
  { id: "likelihood", name: "Likelihood", weight: 25 },
  { id: "urgency", name: "Urgency", weight: 15 },
];

export default function ProSettingsPage() {
  const [rubrics, setRubrics] = useState([]);
  const [timers, setTimers] = useState([]);
  const [name, setName] = useState("My rubric");
  const [criteria, setCriteria] = useState(defaultCriteria);
  const [timerName, setTimerName] = useState("My PF timing");
  const [minutes, setMinutes] = useState(4);
  const load = async () => {
    const options = { credentials: "include" };
    const [rubricResponse, timerResponse] = await Promise.all([
      fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/settings/rubrics`,
        options,
      ),
      fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/settings/timers`,
        options,
      ),
    ]);
    if (rubricResponse.ok) setRubrics(await rubricResponse.json());
    if (timerResponse.ok) setTimers(await timerResponse.json());
  };
  useEffect(() => {
    load();
  }, []);
  const saveRubric = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/settings/rubrics`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name, criteria }),
      },
    );
    if (!response.ok) {
      toast.error("Weights must total 100%.");
      return;
    }
    const rubric = await response.json();
    setRubrics((current) => [...current, rubric]);
    toast.success("Rubric saved.");
  };
  const saveTimer = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/settings/timers`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: timerName,
          timings: { Constructive: minutes * 60 },
        }),
      },
    );
    if (!response.ok) {
      toast.error("Timer values must be between 30 seconds and 15 minutes.");
      return;
    }
    const timer = await response.json();
    setTimers((current) => [...current, timer]);
    toast.success("Timer preset saved.");
  };
  const remove = async (type, id) => {
    await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/settings/${type}/${id}`,
      { method: "DELETE", credentials: "include" },
    );
    await load();
  };
  const total = criteria.reduce((sum, item) => sum + Number(item.weight), 0);
  return (
    <div className="judge-app" data-testid="pro-settings-page">
      <AppHeader onNewRound={() => {}} isPro />
      <main className="settings-page">
        <Link
          className="back-link"
          to="/"
          data-testid="return-to-workspace-from-settings"
        >
          <ArrowLeft size={17} />
          Return to workspace
        </Link>
        <h1>Pro settings</h1>
        <section className="settings-section">
          <h2>Custom rubrics</h2>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            data-testid="rubric-name-input"
          />
          {criteria.map((item) => (
            <div className="criteria-editor" key={item.id}>
              <input
                value={item.name}
                onChange={(event) =>
                  setCriteria((current) =>
                    current.map((criterion) =>
                      criterion.id === item.id
                        ? { ...criterion, name: event.target.value }
                        : criterion,
                    ),
                  )
                }
              />
              <input
                type="number"
                value={item.weight}
                onChange={(event) =>
                  setCriteria((current) =>
                    current.map((criterion) =>
                      criterion.id === item.id
                        ? { ...criterion, weight: Number(event.target.value) }
                        : criterion,
                    ),
                  )
                }
              />
            </div>
          ))}
          <p data-testid="rubric-weight-total">Total: {total}%</p>
          <button
            onClick={() =>
              setCriteria((current) => [
                ...current,
                { id: crypto.randomUUID(), name: "New criterion", weight: 0 },
              ])
            }
            data-testid="add-rubric-criterion-button"
          >
            <Plus size={16} />
            Add criterion
          </button>
          <button onClick={saveRubric} data-testid="save-rubric-button">
            Save rubric
          </button>
          <div className="saved-settings">
            {rubrics.map((rubric) => (
              <div key={rubric.id}>
                <span>{rubric.name}</span>
                <button onClick={() => remove("rubrics", rubric.id)}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </section>
        <section className="settings-section">
          <h2>Custom timer presets</h2>
          <input
            value={timerName}
            onChange={(event) => setTimerName(event.target.value)}
            data-testid="timer-preset-name-input"
          />
          <input
            type="number"
            min="1"
            max="15"
            value={minutes}
            onChange={(event) => setMinutes(Number(event.target.value))}
            data-testid="timer-preset-minutes-input"
          />
          <button onClick={saveTimer} data-testid="save-timer-preset-button">
            Save timer preset
          </button>
          <div className="saved-settings">
            {timers.map((timer) => (
              <div key={timer.id}>
                <span>
                  {timer.name} · {timer.timings.Constructive / 60} min
                  constructive
                </span>
                <button onClick={() => remove("timers", timer.id)}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
